import React, { useState } from 'react';

/**
 * PNG Transparency Test Page
 * 
 * Tests how PNG transparency renders in different scenarios:
 * 1. Raw <img> tag (baseline)
 * 2. Inside colored backgrounds
 * 3. Inside circular containers (OBS style)
 * 4. With different CSS properties
 * 
 * This helps diagnose transparency issues in logos
 */
const PNGTransparencyTest = () => {
  const [testImageUrl, setTestImageUrl] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);

  // Sample test images (you'll replace with real transparent PNGs)
  const sampleImages = [
    {
      name: 'Real Madrid',
      url: `${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/images/teams/madrid.png`,
      expected: 'Transparent background'
    },
    {
      name: 'FC Barcelona',
      url: `${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/images/teams/barcelona.png`,
      expected: 'Transparent background'
    }
  ];

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        setTestImageUrl(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const testUrl = uploadedImage || testImageUrl || sampleImages[0]?.url;

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold mb-2">PNG Transparency Test</h1>
          <p className="text-gray-600">
            Upload a PNG with transparent background to test how it renders in different scenarios
          </p>
        </div>

        {/* Image Selection */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Select Test Image</h2>
          
          {/* Upload Custom Image */}
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Upload PNG:</label>
            <input
              type="file"
              accept=".png"
              onChange={handleFileUpload}
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>

          {/* Or Enter URL */}
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Or Enter URL:</label>
            <input
              type="text"
              value={testImageUrl}
              onChange={(e) => setTestImageUrl(e.target.value)}
              placeholder={`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/images/teams/madrid.png`}
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>

          {/* Sample Images */}
          <div>
            <label className="block text-sm font-semibold mb-2">Or Use Sample:</label>
            <div className="flex gap-2">
              {sampleImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setTestImageUrl(img.url)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {img.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Test Results Grid */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          
          {/* Test 1: Raw Image (Baseline) */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-bold mb-3 text-lg">Test 1: Raw Image (Baseline)</h3>
            <p className="text-sm text-gray-600 mb-3">
              Plain &lt;img&gt; tag, no styling. If transparent, you'll see the white background through it.
            </p>
            <div className="border-2 border-gray-300 p-4 bg-white">
              <img 
                src={testUrl} 
                alt="Test"
                className="w-32 h-32 mx-auto"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              ✅ Expected: White background visible through transparency
            </p>
          </div>

          {/* Test 2: Checkered Background */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-bold mb-3 text-lg">Test 2: Checkered Pattern</h3>
            <p className="text-sm text-gray-600 mb-3">
              Classic transparency indicator. If PNG is truly transparent, you'll see the pattern.
            </p>
            <div 
              className="border-2 border-gray-300 p-4"
              style={{
                backgroundImage: `
                  linear-gradient(45deg, #ccc 25%, transparent 25%),
                  linear-gradient(-45deg, #ccc 25%, transparent 25%),
                  linear-gradient(45deg, transparent 75%, #ccc 75%),
                  linear-gradient(-45deg, transparent 75%, #ccc 75%)
                `,
                backgroundSize: '20px 20px',
                backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
              }}
            >
              <img 
                src={testUrl} 
                alt="Test"
                className="w-32 h-32 mx-auto"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              ✅ Expected: Checkered pattern visible through transparency
            </p>
          </div>

          {/* Test 3: Colored Backgrounds */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-bold mb-3 text-lg">Test 3: Colored Backgrounds</h3>
            <p className="text-sm text-gray-600 mb-3">
              Different background colors. Transparency should show each color.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-black'].map((bg) => (
                <div key={bg} className={`${bg} p-2 rounded`}>
                  <img 
                    src={testUrl} 
                    alt="Test"
                    className="w-16 h-16 mx-auto"
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              ✅ Expected: Each background color visible through transparency
            </p>
          </div>

          {/* Test 4: object-contain */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-bold mb-3 text-lg">Test 4: object-contain</h3>
            <p className="text-sm text-gray-600 mb-3">
              Using object-contain (current approach). Green background to verify transparency.
            </p>
            <div className="border-2 border-gray-300 p-4 bg-green-500">
              <img 
                src={testUrl} 
                alt="Test"
                className="w-32 h-32 mx-auto object-contain"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              ✅ Expected: Green background visible through transparency
            </p>
          </div>

          {/* Test 5: Circular Container (Current OBS Style) */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-bold mb-3 text-lg">Test 5: Circular Dark (OBS Style)</h3>
            <p className="text-sm text-gray-600 mb-3">
              Current implementation: circular with dark background.
            </p>
            <div className="border-2 border-gray-300 p-4 bg-gray-900">
              <div className="w-32 h-32 mx-auto bg-gray-900 rounded-full overflow-hidden flex items-center justify-center">
                <img 
                  src={testUrl} 
                  alt="Test"
                  className="w-full h-full object-contain p-2"
                  style={{ backgroundColor: 'transparent' }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              ⚠️ Problem: Dark bg on dark overlay = hard to see
            </p>
          </div>

          {/* Test 6: Circular Container White */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-bold mb-3 text-lg">Test 6: Circular White</h3>
            <p className="text-sm text-gray-600 mb-3">
              Alternative: circular with white background.
            </p>
            <div className="border-2 border-gray-300 p-4 bg-gray-900">
              <div className="w-32 h-32 mx-auto bg-white rounded-full overflow-hidden flex items-center justify-center">
                <img 
                  src={testUrl} 
                  alt="Test"
                  className="w-full h-full object-contain p-2"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              ✅ Expected: White circle visible, logo on top
            </p>
          </div>

          {/* Test 7: Transparent Container */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-bold mb-3 text-lg">Test 7: No Background (Transparent)</h3>
            <p className="text-sm text-gray-600 mb-3">
              Truly transparent - no background at all. Black overlay behind.
            </p>
            <div className="border-2 border-gray-300 p-4 bg-black">
              <div className="w-32 h-32 mx-auto rounded-full overflow-hidden flex items-center justify-center">
                <img 
                  src={testUrl} 
                  alt="Test"
                  className="w-full h-full object-contain p-2"
                  style={{ backgroundColor: 'transparent' }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              ⚠️ Expected: Logo directly on black, no circle visible
            </p>
          </div>

          {/* Test 8: With inline backgroundColor */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="font-bold mb-3 text-lg">Test 8: Explicit Transparent Style</h3>
            <p className="text-sm text-gray-600 mb-3">
              Image with style backgroundColor: 'transparent'. Pink background to test.
            </p>
            <div className="border-2 border-gray-300 p-4 bg-pink-500">
              <img 
                src={testUrl} 
                alt="Test"
                className="w-32 h-32 mx-auto"
                style={{ backgroundColor: 'transparent' }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              ✅ Expected: Pink background visible through transparency
            </p>
          </div>

        </div>

        {/* OBS Simulation */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">OBS Overlay Simulation</h2>
          <p className="text-sm text-gray-600 mb-4">
            This simulates how the logo appears in the actual OBS scoreboard overlay.
          </p>
          
          <div className="bg-black/90 text-white p-8 rounded-lg">
            <div className="flex items-center justify-center gap-12">
              {/* Home Team */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-gray-900 rounded-full overflow-hidden flex items-center justify-center">
                  <img 
                    src={testUrl}
                    alt="Home"
                    className="w-full h-full object-contain p-2"
                    style={{ backgroundColor: 'transparent' }}
                  />
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-400">HOME</div>
                  <div className="text-5xl font-black">2</div>
                </div>
              </div>

              <div className="text-3xl font-bold text-gray-400">-</div>

              {/* Away Team */}
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-sm text-gray-400">AWAY</div>
                  <div className="text-5xl font-black">1</div>
                </div>
                <div className="w-20 h-20 bg-gray-900 rounded-full overflow-hidden flex items-center justify-center">
                  <img 
                    src={testUrl}
                    alt="Away"
                    className="w-full h-full object-contain p-2"
                    style={{ backgroundColor: 'transparent' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Diagnostic Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-6">
          <h3 className="font-bold mb-2">🔍 Diagnostic Checklist</h3>
          <ul className="text-sm space-y-1">
            <li>✅ Test 2 (Checkered): Can you see the pattern through the logo?</li>
            <li>✅ Test 3 (Colors): Do all 6 background colors show through?</li>
            <li>✅ Test 7 (Transparent): Is the logo directly on black with no white square?</li>
            <li>⚠️ If you see white squares: PNG is NOT truly transparent</li>
            <li>⚠️ If transparency works in tests 1-4 but NOT in OBS simulation: CSS issue</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PNGTransparencyTest;
